import { useEffect } from 'react';

/**
 * Hook to detect Android devices with navigation bar and apply appropriate classes
 * Handles safe area insets for Android 10+ (API level 29+) devices with
 * on-screen navigation buttons in portrait mode.
 *
 * The hook:
 * 1. Detects Android device and version
 * 2. Adds 'android-nav-present' class for Android 10+ devices
 * 3. Sets viewport height to handle safe area correctly
 */
export const useAndroidNavBar = () => {
  useEffect(() => {
    // Detect Android device and version
    const userAgent = navigator.userAgent;
    const isAndroid = /Android/i.test(userAgent);
    const androidVersion = isAndroid ? parseFloat(userAgent.match(/Android\s([0-9.]+)/)?.[1] || '0') : 0;

    // Get device pixel ratio to help determine if device likely has navigation bar
    const devicePixelRatio = window.devicePixelRatio || 1;

    const tg = window.Telegram?.WebApp;
    const version = tg ? parseFloat(tg.version) : 0;

    // Only apply for Android 10+ (API level 29+) with likely navigation bar
    const shouldApplyNavBar = isAndroid && androidVersion >= 10 && devicePixelRatio >= 2.5; // Most modern Android devices with nav bar have higher DPR

    if (shouldApplyNavBar) {
      document.body.classList.add('android-nav-present');

      // Set initial viewport height
      document.body.style.height = window.innerHeight + 'px';
    }

    if (isAndroid && version >= 8.0) document.body.classList.add('android-and-fullscreen');

    // Handle viewport height changes
    const handleResize = () => {
      if (shouldApplyNavBar) {
        document.body.style.height = window.innerHeight + 'px';
      }
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.classList.remove('android-nav-present');
      document.body.classList.remove('android-and-fullscreen');
      document.body.style.removeProperty('height');
    };
  }, []);
};
